import { FULL_HD, HD, RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import _memoize from 'lodash/memoize';
import { makeThemeColor, PRIMARY, TERTIARY } from '@tornado-rdk/theme';
import { FONT, getFont } from '@tornado-rdk/typography';
import { colors as progressColors, HEIGHT as PROGRESS_HEIGHT } from '../progress/progress.style';
import { cacheResolver, makeDefaultStylingContext } from '../utils/style';
import { TRANSPARENT } from '../utils/colors';
import {
  BORDER_COLOR,
  BORDER_RADIUS,
  BORDER_WIDTH,
  INNER_BORDER_COLOR,
  SCALE_FOCUSED,
  FOCUSED_BORDER_ALPHA,
  UNFOCUSED_BORDER_ALPHA,
} from '../utils/focus';
import { ICONS, sizes, templates } from './portrait.constants';
import { middle } from '../utils/sizes';

export { PROGRESS_HEIGHT };

export const defaultContext = makeDefaultStylingContext();

export const ICON_HEIGHT = {
  [HD]: 24,
  [FULL_HD]: 32,
};

export const PADDING_VERTICAL = 4;

export const PADDING_HORIZONTAL = 8;

export const TEXT_MARGIN_BOTTOM = 8;

export const colors = {
  text: makeThemeColor(PRIMARY),
  overlay: makeThemeColor(TERTIARY),
};

export const getIconViewBox = _memoize(screenSize => ({
  h: ICON_HEIGHT[screenSize],
}));

export const getIcon = (icons, position) => (icons ? icons[ICONS[position]] : null);

export const getInitialStyle = ({ screenSize, colorMode } = defaultContext) => {
  const { w, h } = sizes[templates.Portrait][RENDERING_CONTEXT.GENERAL][screenSize];
  const borderSize = BORDER_WIDTH[screenSize];
  const borderOffset = borderSize * 2;

  return {
    w,
    h,
    Border: {
      alpha: 0,
      w: w + borderOffset,
      h: h + borderOffset,
      x: -borderSize,
      y: -borderSize,
      color: TRANSPARENT,
      shader: {
        radius: BORDER_RADIUS[screenSize],
        outline: BORDER_WIDTH[screenSize],
        strokeColor: TRANSPARENT,
        innerColor: TRANSPARENT,
      },
    },
    Image: {
      w,
      h,
      shader: {
        radius: BORDER_RADIUS[screenSize],
        progressHeight: PROGRESS_HEIGHT,
        progressBackgroundColor: progressColors.background[colorMode],
        progressBarColor: progressColors.bar[colorMode],
        overlayColor: colors.overlay[colorMode],
      },
    },
    Text: {
      x: Math.round(w / 2),
      y: h - PROGRESS_HEIGHT - TEXT_MARGIN_BOTTOM,
      mountX: 0.5,
      mountY: 1,
      color: colors.text[colorMode],
      text: {
        ...getFont(FONT.BOSTON_1, screenSize),
        textAlign: 'center',
        verticalAlign: 'middle',
        wordWrapWidth: w - PADDING_HORIZONTAL * 2,
      },
    },
    TopLeftIcon: {
      x: PADDING_HORIZONTAL,
      y: PADDING_VERTICAL,
    },
    TopRightIcon: {
      alpha: 0.8,
      mountX: 1,
      x: w,
      y: PADDING_VERTICAL,
    },
    BottomRightIcon: {
      mount: 1,
      x: w,
      y: h - PROGRESS_HEIGHT - PADDING_VERTICAL,
    },
    Incentive: {
      x: middle,
      y: middle,
      mount: 0.5,
    },
  };
};

export const getFocusedStyle = _memoize(
  ({ colorMode }) => ({
    Border: {
      smooth: {
        alpha: FOCUSED_BORDER_ALPHA,
        scale: SCALE_FOCUSED,
        'shader.strokeColor': BORDER_COLOR[colorMode],
        'shader.innerColor': INNER_BORDER_COLOR[colorMode],
        'shader.scale': SCALE_FOCUSED,
      },
    },
    Image: {
      smooth: {
        scale: SCALE_FOCUSED,
        'shader.scale': SCALE_FOCUSED,
      },
    },
  }),
  cacheResolver,
);

export const unFocusedStyle = {
  Border: {
    smooth: {
      alpha: UNFOCUSED_BORDER_ALPHA,
      scale: 1,
      'shader.strokeColor': TRANSPARENT,
      'shader.innerColor': TRANSPARENT,
      'shader.scale': 1.0,
    },
  },
  Image: {
    smooth: {
      scale: 1,
      'shader.scale': 1.0,
    },
  },
};
